const TRACK_FOCUS_COLORS = [
  '#FFAB00',
  '#00897B',
  '#FF8F00',
  '#8E24AA',
  '#E53935',
  '#3949AB',
  '#1E88E5',
  '#43A047',
  '#6D4C41',
  '#00ACC1'
];

export const getTrackColor = (trackId, preferredColor = null) => {
  const id = String(trackId || '');
  if (!id) return null;
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash << 5) - hash + id.charCodeAt(index);
    hash |= 0;
  }
  const colorIndex = Math.abs(hash) % TRACK_FOCUS_COLORS.length;
  if (colorIndex === 0 && preferredColor) return preferredColor;
  return TRACK_FOCUS_COLORS[colorIndex];
};

export const getTrackColorKeyFromFeature = feature => {
  if (!feature) return '';
  const properties = feature.properties || {};
  return (
    properties.EntityId ||
    properties.featureId ||
    properties.trackId ||
    feature.id ||
    ''
  );
};

/**
 * "Full tracks - colour coded" hour-of-day colour, aka the "sky gradient"
 * (per Dr J's feedback): black night, vivid yellow morning, white midday,
 * vivid red afternoon, via four keyframes - midnight, dawn, noon, dusk -
 * interpolated in between.
 *
 * Deliberately NOT a mirrored day/night curve: dawn (06:00) and dusk
 * (18:00) are given different colours on purpose (dawn: vivid yellow;
 * dusk: vivid red), so:
 *   - 00:00-06:00 "einde van de nacht": fades black -> yellow
 *   - 06:00-12:00 morning: fades yellow -> white, through more shades of
 *     yellow as it lightens (reads as "the yellow half")
 *   - 12:00-18:00 afternoon: fades white -> red, through more shades as it
 *     deepens (reads as "the red half")
 *   - 18:00-24:00 "begin nacht": fades red -> black
 * This keeps morning and afternoon visually distinct at a glance (yellow
 * vs red family) while still reading smoothly, with noon as a bright
 * shared midpoint rather than a hard flip.
 *
 * Hue is stored "unwrapped" (allowed to run past 360) on each keyframe, so
 * interpolating between two keyframes always sweeps the intended arc of
 * the colour wheel - e.g. dawn -> noon -> dusk stays within the warm
 * red-orange-yellow band, never crossing into green. Midnight and noon
 * lean on saturation/lightness (near-black, near-white) rather than hue to
 * read as black/white, since hue barely matters at the extremes of
 * lightness - the 0h and 24h keyframes must stay identical so the loop has
 * no visible seam at midnight.
 */
const HOUR_COLOUR_KEYFRAMES = [
  { hour: 0, hue: 390, saturation: 15, lightness: 4 }, // midnight - black
  { hour: 6, hue: 415, saturation: 92, lightness: 55 }, // dawn - vivid yellow
  { hour: 12, hue: 410, saturation: 25, lightness: 92 }, // noon - white
  { hour: 18, hue: 365, saturation: 92, lightness: 50 }, // dusk - vivid red
  { hour: 24, hue: 390, saturation: 15, lightness: 4 } // back to midnight
];

/**
 * @param {number} hour - hour of day, 0-23 (or any number, wrapped mod 24).
 * @returns {string} an hsl() colour string.
 */
export const getHourColor = hour => {
  const h = ((Number(hour) % 24) + 24) % 24;
  let lowerIndex = 0;
  for (let index = 0; index < HOUR_COLOUR_KEYFRAMES.length - 1; index += 1) {
    if (
      h >= HOUR_COLOUR_KEYFRAMES[index].hour &&
      h <= HOUR_COLOUR_KEYFRAMES[index + 1].hour
    ) {
      lowerIndex = index;
      break;
    }
  }
  const lower = HOUR_COLOUR_KEYFRAMES[lowerIndex];
  const upper = HOUR_COLOUR_KEYFRAMES[lowerIndex + 1];
  const span = upper.hour - lower.hour;
  const t = span > 0 ? (h - lower.hour) / span : 0;
  const hue = (((lower.hue + (upper.hue - lower.hue) * t) % 360) + 360) % 360;
  const saturation =
    lower.saturation + (upper.saturation - lower.saturation) * t;
  const lightness = lower.lightness + (upper.lightness - lower.lightness) * t;
  return `hsl(${hue.toFixed(1)}, ${saturation.toFixed(1)}%, ${lightness.toFixed(
    1
  )}%)`;
};

/**
 * Pairs a track feature's coordinates with their per-point timestamps, so
 * each GPS fix's local hour-of-day can drive its colour. Mirrors the
 * dateTimes/DateTimes lookup already used in actualTracks.js.
 *
 * The hour is read directly from the ISO datetime string (e.g. the "14" in
 * "2026-07-17T14:32:00+02:00") rather than via `new Date(...).getHours()`,
 * deliberately: that would convert to the viewer's browser timezone, but
 * what matters here is whether it was locally day or night *at the ranger's
 * location*, not where Claude/Dr J happens to be looking at the map from.
 * @param {object} feature - a GeoJSON Feature (LineString or MultiPoint).
 * @returns {Array<{coordinate: number[], hour: number, timestamp: number}>}
 *   sorted by timestamp.
 */
export const extractTimedCoordinates = feature => {
  if (!feature || !feature.geometry) return [];
  const { geometry } = feature;
  const props = feature.properties || {};
  const coordinates =
    geometry.type === 'MultiPoint' || geometry.type === 'LineString'
      ? geometry.coordinates
      : [];
  const dateTimeCandidates = [
    feature.dateTimes,
    feature.DateTimes,
    props.dateTimes,
    props.DateTimes,
    props.timestamps,
    props.Timestamps
  ];
  // The backend hands back DateTimes as one comma-joined string (e.g.
  // "2026-05-06T11:16:18+0200,2026-05-06T11:16:18+0200"), not an array -
  // confirmed live via $r.props.fullTrackTraces during debugging. Handle
  // both shapes, splitting the string form into one entry per coordinate.
  const dateTimesCandidate = dateTimeCandidates.find(
    candidate =>
      Array.isArray(candidate) ||
      (typeof candidate === 'string' && candidate.trim())
  );
  const dateTimes = Array.isArray(dateTimesCandidate)
    ? dateTimesCandidate
    : typeof dateTimesCandidate === 'string'
    ? dateTimesCandidate
        .split(',')
        .map(value => value.trim())
        .filter(Boolean)
    : null;
  if (
    !Array.isArray(coordinates) ||
    !Array.isArray(dateTimes) ||
    coordinates.length !== dateTimes.length
  ) {
    return [];
  }
  return coordinates
    .map((coordinate, index) => {
      if (!Array.isArray(coordinate) || coordinate.length < 2) return null;
      const raw = String(dateTimes[index] || '').trim();
      const match = raw.match(/T(\d{2}):/);
      if (!match) return null;
      const normalized = raw.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
      const timestamp = Date.parse(normalized);
      if (!Number.isFinite(timestamp)) return null;
      return { coordinate, hour: Number(match[1]), timestamp };
    })
    .filter(Boolean)
    .sort((a, b) => a.timestamp - b.timestamp);
};
